import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import AppSidebar from '../components/common/AppSidebar';

const AboutPage = () => {
  const stats = [
    { number: '4+', label: 'Star Rating Threshold' },
    { number: '100%', label: 'Verified Reviews' },
    { number: '0', label: 'Hidden Fees' },
    { number: '24/7', label: 'AI Assistance' }
  ];

  const features = [
    {
      title: 'AI-powered matching',
      description: 'Intelligent, personalised recommendations based on your specific situation.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
      )
    },
    {
      title: 'Only top-rated solicitors',
      description: 'We work exclusively with firms rated 4 stars or above across Google, Trustpilot, and our own verified reviews system.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
        </svg>
      )
    },
    {
      title: 'Verified Zahoot Reviews',
      description: 'Clients can leave authentic, verified feedback directly through Zahoot AI.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
      )
    },
    {
      title: 'Transparency built in',
      description: 'No inflated fees or misleading listings. We operate on a fair, transparent model.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
        </svg>
      )
    }
  ];
  
  // Set data-page attribute on body for CSS targeting
  useEffect(() => {
    document.body.setAttribute('data-page', 'home');
    
    return () => {
      document.body.removeAttribute('data-page');
    };
  }, []);

  return (
    <>
      <SEO 
        fallbackTitle="About Zahoot AI"
        fallbackDescription="Zahoot AI is a smart, independent platform built to help individuals and businesses find the right legal partner - quickly, confidently, and without the guesswork."
      />
      <div className="home-page">
        {/* Sidebar Navigation */}
        <AppSidebar />
        
        <main className="home-page__main">
          <div className="max-w-6xl w-full px-4 lg:right-9 sm:px-6 lg:px-8 mx-auto z-10 relative">
            <div className="text-center mb-12 z-10 relative pt-8">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
                About <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D80364] to-[#F0654B]">Zahoot AI</span>
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                A smart, independent platform connecting you with the right legal partner
              </p>
            </div>

            <div className="bg-purple-900/50 rounded-xl p-8 shadow-lg mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  Zahoot AI is a smart, independent platform built to help individuals and businesses find 
                  the right legal partner - quickly, confidently, and without the guesswork.
                </p>
                <p>
                  We understand that navigating legal services can feel overwhelming. That's why we've
                  trained our AI on the latest UK legal data and legal industry insights - so it can ask 
                  the right questions, understand your unique needs, and guide you towards trusted, 
                  high-performing solicitors.
                </p>
                <p>
                  Whether you're seeking help for a personal matter or a business issue, Zahoot AI ensures 
                  you're matched with solicitors who meet your specific legal, budget, and sector requirements.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {stats.map((stat, index) => (
                <div key={index} className="bg-purple-900/50 rounded-xl p-6 text-center shadow-lg">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D80364] to-[#F0654B] mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-300 text-sm md:text-base">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">How We're Different</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <div key={index} className="bg-purple-900/50 rounded-xl p-6 shadow-lg">
                    <div className="w-12 h-12 bg-purple-500/30 rounded-full flex items-center justify-center mb-4 mx-auto text-purple-300">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white text-center mb-3">{feature.title}</h3>
                    <p className="text-gray-300 text-center text-sm">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-purple-900/50 rounded-xl p-8 shadow-lg mb-12">
              <div className="space-y-4 text-gray-300">
                <p>
                  Zahoot AI operates on a fair, transparent model. We may work with solicitors through 
                  a subscription or referral basis, but this never affects what you pay or who we recommend.
                </p>
                <p>
                  We're here to bring clarity and confidence to your legal search—powered by technology, 
                  backed by integrity.
                </p>
                <p className="text-center text-white font-bold text-xl mt-8">
                  Zahoot AI. Discover. Decide. Done.
                </p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AboutPage; 